import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DateService } from 'src/app/common/services/date.service';
import { DateRangeId } from 'src/app/invoice/models/enums/invoice.enum';
import { DateRangeComponent } from 'src/app/library/date-range/date-range.component';

@Component({
  selector: 'custom-date-selector',
  templateUrl: './custom-date-selector.component.html',
  styleUrls: ['./custom-date-selector.component.css'],
})
export class CustomDateSelectorComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('customDate', { static: false }) customDate!: DateRangeComponent;
  @ViewChild('customDateWrapper', { static: false }) customDateWrapper: ElementRef<any>;
  @ViewChild('dateRangeTrigger', { static: false }) dateRangeTrigger!: ElementRef;
  today: Date = new Date();
  dateRange = [];
  @Input() selected = 'today';
  @Input() width = 280;
  @Input() customDisabledDate: (date: Date) => boolean;
  @Input() excludeDateOptions: string[] = [];
  @Input() isEodReport?: boolean = false;
  dateRangeIds = DateRangeId
  selectedOptionName: string = '';
  dateRangeText: string;
  dateSelectOptions = [
    { id: DateRangeId.TODAY, name: 'Today', range: '' },
    { id: DateRangeId.YESTERDAY, name: 'Yesterday', range: '' },
    { id: DateRangeId.LAST_WEEK, name: 'Last Week', range: '' },
    { id: DateRangeId.THIS_MONTH, name: 'This Month', range: '' },
    { id: DateRangeId.LAST_MONTH, name: 'Last Month', range: '' },
    { id: DateRangeId.LAST_7_DAYS, name: 'Last 7 days', range: '' },
    { id: DateRangeId.LAST_30_DAYS, name: 'Last 30 days', range: '' },
    { id: DateRangeId.LAST_90_DAYS, name: 'Last 90 days', range: '' },
  ];
  date: {
    type: string;
    startDate: string;
    endDate: string;
  };
  @Output() onDateChange = new EventEmitter();

  translationText;
  subscription = new Subscription();

  constructor(
    private _dateService: DateService,
    private translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if(this.isEodReport) {
      this.selected = DateRangeId.TODAY;
      this.updateDateSelection();
      return;
    }
    const currSelectedDate = changes?.selected?.currentValue;
    const prevSelectedDate = changes?.selected?.previousValue;
    if (changes?.excludeDateOptions && this.translationText) {
      this.buildDateSelectOptions(this.translationText);
    }
    if(currSelectedDate && currSelectedDate !== prevSelectedDate) { 
      this.updateDateSelection();
    }
  }

  ngOnInit() {
    this.subscription.add(
      this.translateService.get(['DATES_ORDER']).subscribe((res) => {
        this.translationText = res.DATES_ORDER;
        this.buildDateSelectOptions(this.translationText);
        this.updateDateSelection();
      })
    );
  }

  private buildDateSelectOptions(translations): void {
    const allOptions = [
      { id: DateRangeId.TODAY, name: translations.TODAY, range: '' },
      { id: DateRangeId.YESTERDAY, name: translations.YESTERDAY, range: '' },
      { id: DateRangeId.LAST_WEEK, name: translations.LAST_WEEK, range: '' },
      { id: DateRangeId.THIS_MONTH, name: translations.MONTH, range: '' },
      { id: DateRangeId.LAST_MONTH, name: translations.LAST_MONTH, range: '' },
      { id: DateRangeId.LAST_7_DAYS, name: translations.S_DAYS, range: '' },
      { id: DateRangeId.LAST_30_DAYS, name: translations.T_DAYS, range: '' },
      { id: DateRangeId.LAST_90_DAYS, name: translations.N_DAYS, range: '' },
    ];

    this.dateSelectOptions = allOptions.filter(
      (option) => !this.excludeDateOptions.includes(option.id)
    );
  }

  private updateDateSelection(): void {
    this.selectedDateChanged(this.selected);
  }

  disabledDate = (current: Date): boolean => {
    if (this.customDisabledDate) {
      return this.customDisabledDate(current);
    }
    return this._dateService.isFutureDate(current);
  };

  selectedDateChanged(e) {
    const selectedOption = this.dateSelectOptions.find(option => option?.id === e);
    this.selectedOptionName = selectedOption ? selectedOption?.name : '';
  
    if (e && e !== DateRangeId.CUSTOM) {
      this.date = this._dateService.getDateRange(e);
      this.dateRangeText = this.returnDateRangeText(this.date);
      this.onDateChange.emit(this.date);
    } else if (e === DateRangeId.CUSTOM) {
      this.openRangePicker();
    } else {
      this.onDateChange.emit({ type: null, startDate: '', endDate: '' });
    }
  }

  openRangePicker() {
    this.selected = DateRangeId.CUSTOM;
    this.selectedOptionName = this.translationText.CUSTOM;
    this.dateRangeText = '';
    this.triggerRangePicker();
  }

  triggerRangePicker() {
    this.customDate.openDatePicker(this.customDateWrapper); 
  }

  returnDateRangeText(date) {
    if (date) {
      if (date.startDate != date.endDate) {
        return `(${this._dateService.convertReadableDate(
          date.startDate
        )} - ${this._dateService.convertReadableDate(date.endDate)})`;
      } else
        return `(${this._dateService.convertReadableDate(date.startDate)})`;
    }
  }

  onChange(result): void {
    const startDate = this._dateService.returnFormatedDate(result?.startDate);
    const endDate = this._dateService.returnFormatedDate(result?.endDate);
    this.date = {
      startDate: startDate,
      endDate: endDate,
      type: DateRangeId.CUSTOM,
    };
    this.dateRangeText = this.returnDateRangeText(this.date);
    this.onDateChange.emit(this.date);
  }

  openChange(e) {
    if (e === false && this.selected === DateRangeId.CUSTOM) {
      this.triggerRangePicker();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
